<script setup lang="ts">
import { computed, onMounted, type Ref, ref, watch } from 'vue'
import Plotly from 'plotly.js-dist'
import { useSectionVisualisationStore } from '@/store/sectionVisualization'
import { onLegendClicked } from './utils/legendClicked'
import type { Layout, PlotlyHTMLElement, Config, Annotations } from 'plotly.js'
import { storeToRefs } from 'pinia'
import {
  createPlotlyGeoprofileVisualizationData,
  type GTLPSectionVisualisation
} from '@haskoning/geoprofile-visualization'

const props = defineProps({
  showLegend: {
    type: Boolean,
    default: false
  },
  extraData: {
    type: Array as () => any[],
    default: () => []
  },
  xAxisRange: {
    required: false,
    default: () => [-50, 50]
  },
  yAxisRange: {
    required: false,
    default: () => [-5, 20]
  },
})

const giDataIcon = {
  svg: '<svg xmlns="http://www.w3.org/2000/svg" fill="#c3c4c5" viewBox="0 0 24 24"><title>map-marker-multiple</title><path d="M14,11.5A2.5,2.5 0 0,0 16.5,9A2.5,2.5 0 0,0 14,6.5A2.5,2.5 0 0,0 11.5,9A2.5,2.5 0 0,0 14,11.5M14,2C17.86,2 21,5.13 21,9C21,14.25 14,22 14,22C14,22 7,14.25 7,9A7,7 0 0,1 14,2M5,9C5,13.5 10.08,19.66 11,20.81L10,22C10,22 3,14.25 3,9C3,5.83 5.11,3.15 8,2.29C6.16,3.94 5,6.33 5,9Z" /></svg>'
}

const plotly_layout = computed<Partial<Layout>>(() => {
  return {
    showlegend: true,
    dragmode: 'pan',
    autosize: true,
    margin: { l: 50, r: 10, b: 80, t: 10, pad: 4 },
    legend: { x: 100, y: 1, orientation: 'v', bgcolor: '#f7f8fa' },
    xaxis: {
      title: 'Distance along section line [meters]',
      range: props.xAxisRange,
      nticks: 20,
      showspikes: true,
      spikemode: 'across',
      spikesnap: 'cursor'
    },
    yaxis: {
      title: 'Elevation [meters] ',
      range: props.yAxisRange,
      nticks: 20,
      tickmode: 'linear',
      showspikes: true,
      spikemode: 'across'
    }
  }
})

const visualizeGiData = ref<boolean>(true)

const options = computed<Partial<Config>>(() => {
  return {
    responsive: true,
    modeBarButtonsToAdd: [
      {
        title: '',
        name: `Gi Data`,
        icon: giDataIcon,
        direction: 'up',
        click: () => {
          visualizeGiData.value = !visualizeGiData.value
          relayout()
        }
      }
    ],
    scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  }
})

const emit = defineEmits(['set-all-traces', 'set-layout'])

const sectionVisualisationStore = useSectionVisualisationStore()
const { currentItem, boreholeWidthScaling } = storeToRefs(sectionVisualisationStore)

const adjustedXData = (data: number[][], widthScaling: number): number[][] => {
  const xData = data.map((coord) => coord[0])
  const centerX = (Math.max(...xData) + Math.min(...xData)) / 2
  data = data.map((coord) => [centerX + ((coord[0] - centerX) * widthScaling) / 100, coord[1]])
  return data
}

const plotlyChart: Ref<PlotlyHTMLElement | null> = ref(null)
const isCurrentItemNotEmpty = computed(() => !!currentItem.value)

const resetChart = (width: number, height: number) => {
  if (plotlyChart.value) {
    Plotly.relayout(plotlyChart.value, {
      height: height,
      width: width
    })
  }
}

defineExpose({ resetChart })

const createTraceData = (data: any) => {
  const traces = [...props.extraData]
  let layout: any = structuredClone(plotly_layout.value)
  if (data) {
    const visualizationData = JSON.parse(JSON.stringify(data)) as GTLPSectionVisualisation
    const { borehole_logs } = visualizationData
    borehole_logs.forEach((log) => {
      log.element_visualizations.forEach((visualization) => {
        visualization.log_polygon.coordinates[0] = adjustedXData(
          visualization.log_polygon.coordinates[0],
          boreholeWidthScaling.value
        )
      })
    })

    const SHIFTING_DISTANCE = -200

    const { dataTraces, layout, labelTraces } = createPlotlyGeoprofileVisualizationData({
      geoprofileData: visualizationData,
      locationIdColorMapping: {},
      profileDistanceCorrection: SHIFTING_DISTANCE
    })
    layout.annotations = labelTraces as Partial<Annotations>[]
    if (visualizeGiData.value) {
      traces.push(...dataTraces)
      layout.annotations = labelTraces as Partial<Annotations>[]
    } else {
      layout.annotations = []
    }
  }
  return { dataTraces: traces, layout }
}

const createPlot = (giVisualizationData: GTLPSectionVisualisation) => {
  if (plotlyChart.value) {
    const { dataTraces, layout } = createTraceData(giVisualizationData)
    Plotly.newPlot(plotlyChart.value, dataTraces, layout, options.value).then(() =>
      onLegendClicked(plotlyChart)
    )
  }
}

watch(currentItem, (newVal) => {
  if (newVal) {
    createPlot(newVal)
  }
})

watch(
  () => props.extraData,
  () => {
    if (props.extraData && currentItem.value) {
      createPlot(currentItem.value)
    }
  }
)

const relayout = () => {
  if (plotlyChart.value) {
    const { dataTraces, layout } = createTraceData(currentItem.value)
    Plotly.react(plotlyChart.value, dataTraces, layout, options.value)
  }
}

watch(
  () => props.extraData,
  () => relayout()
)

watch(
  () => boreholeWidthScaling.value,
  () => relayout()
)

watch(
  () => props.showLegend,
  (newVal) => {
    if (plotlyChart.value) {
      Plotly.relayout(plotlyChart.value, { showlegend: newVal })
    }
  }
)

watch(
  () => props.xAxisRange,
  () => relayout(),
  { deep: true }
)

watch(
  () => props.yAxisRange,
  () => relayout(),
  { deep: true }
)

onMounted(() => {
  const { dataTraces, layout } = createTraceData(currentItem.value)
  if (plotlyChart.value) {
    Plotly.newPlot(plotlyChart.value, dataTraces, layout, options.value).then(() =>
      onLegendClicked(plotlyChart)
    )
  }
})
</script>

<template>
  <div ref="plotlyChart" />
</template>
<style scoped>
.range-slider {
  position: absolute;
  bottom: 0;
  right: 25px;
  z-index: 100;
}

:deep(.v-slider-thumb__label) {
  bottom: -50px !important;
}

:deep(.v-slider-thumb__label::before) {
  content: none;
}
</style>
